import { applyBoardSquareRoll, getBoardSquare } from './doorBoard';
import { recordThrowEvent } from './throwCombo';
import { getBoardEncounterRound } from './runConstants';
import { boostPowerUpRoll } from './powerUp';

// Resolve clockwise from the landing: up, right, down, left. Never wrap rows.
export const getChainReactionIndices = (index) => {
  if (!Number.isInteger(index) || index < 0 || index >= 25) return [];
  const row = Math.floor(index / 5);
  const column = index % 5;
  return [
    index,
    row > 0 ? index - 5 : null,
    column < 4 ? index + 1 : null,
    row < 4 ? index + 5 : null,
    column > 0 ? index - 1 : null,
  ].filter((cell) => cell !== null);
};

export const getChainReactionState = (game) => {
  const owned = !!game.relics?.includes('chainReaction');
  const used =
    !!game.chainReaction?.used ||
    Number.isInteger(game.chainReaction?.usedFloor);
  const available = owned && !used;
  return {
    owned,
    used,
    available,
  };
};

export const commitChainReaction = (game, landingIndex) => {
  if (
    !['rolled', 'bossPlayerRolled', 'bossRolled'].includes(game.phase) ||
    !getChainReactionState(game).available ||
    !getBoardSquare(game.doorBoard, landingIndex)
  )
    return game;
  return {
    ...recordThrowEvent(game, 'chain', {
      tiles: getChainReactionIndices(landingIndex).length,
    }),
    chainReaction: { used: true, usedFloor: game.floor },
    chainReactionActivation: { floor: game.floor, landingIndex },
  };
};

export const hasChainReaction = (game) =>
  !!game.chainReactionActivation &&
  game.chainReactionActivation.floor === game.floor &&
  game.chainReactionActivation.landingIndex === game.landingIndex &&
  getChainReactionIndices(game.landingIndex).length > 0;

export const getTriggeredBoardSquares = (game) => {
  const resolution = game.boardTileResolution;
  if (
    resolution?.floor === game.floor &&
    resolution.landingIndex === game.landingIndex &&
    resolution.round === getBoardEncounterRound(game) &&
    Array.isArray(resolution.entries)
  )
    return resolution.entries;
  const indices = hasChainReaction(game)
    ? getChainReactionIndices(game.landingIndex)
    : [game.landingIndex];
  return indices
    .map((index) => ({ index, square: getBoardSquare(game.doorBoard, index) }))
    .filter(({ square }) => !!square);
};

export const getBoardRollSteps = (game, initialRoll) => {
  let value = initialRoll;
  return getTriggeredBoardSquares(game).map(
    ({ index, square, powerUpMultiplier }) => {
      const before = value;
      value = powerUpMultiplier
        ? boostPowerUpRoll(value, powerUpMultiplier)
        : applyBoardSquareRoll(value, square);
      return { index, square, before, value };
    }
  );
};

export const getBoardEffectTotal = (game, type) =>
  getTriggeredBoardSquares(game).reduce(
    (total, { square }) =>
      total + (square.effect?.type === type ? square.effect.amount || 0 : 0),
    0
  );

export const getChainReactionActivation = (game) =>
  hasChainReaction(game)
    ? {
        id: 'chainReaction',
        bonus: 0,
        message: `Triggered ${
          getTriggeredBoardSquares(game).length
        } tiles: landing, then up, right, down, left. Each tile resolves once.`,
      }
    : null;
